/**
 * CalendarActionsContext - Stable Actions/Callbacks
 * Provides stable action functions that don't cause rerenders
 * Uses useCallback with stable dependencies
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useCalendarState } from './CalendarStateContext';
import { DEFAULT_FILTERS, FILTER_TYPES } from '../constants/filterOptions';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { updateUserPreferencesForFilters, resetFilterPreferences, updateFirmUserPreference } from '../utils/filterPersistence';

const CalendarActionsContext = createContext(undefined);

/**
 * Hook to access calendar actions
 * @returns {object} Calendar action functions
 * @throws {Error} If used outside CalendarActionsProvider
 */
export const useCalendarActions = () => {
  const context = useContext(CalendarActionsContext);
  if (!context) {
    throw new Error('useCalendarActions must be used within CalendarActionsProvider');
  }
  return context;
};

/**
 * Calendar Actions Provider
 * Provides stable action callbacks to child components
 */
export const CalendarActionsProvider = ({ children }) => {
  const { setFilters, setSelectedUsers, setGoogleEventsEnabled, setSettings, isGlobal } = useCalendarState();
  const userContext = useUserDetailsContext();

  const applyCategoryConstraints = useCallback((nextFilters) => {
    const category = nextFilters[FILTER_TYPES.CATEGORY];
    if (category === 'task' || category === 'sol') {
      return {
        ...nextFilters,
        [FILTER_TYPES.LOCATION]: '',
        [FILTER_TYPES.EVENT_TYPE]: '',
        [FILTER_TYPES.CONFERENCE_ROOM]: '',
        [FILTER_TYPES.FIRM_SCOPE]: false,
      };
    }

    if (category === 'dob') {
      return {
        ...nextFilters,
        [FILTER_TYPES.LOCATION]: '',
        [FILTER_TYPES.PRACTICE_AREA]: '',
        [FILTER_TYPES.EVENT_TYPE]: '',
        [FILTER_TYPES.CONFERENCE_ROOM]: '',
        [FILTER_TYPES.FIRM_SCOPE]: false,
      };
    }

    return nextFilters;
  }, []);

  // Filter Actions - STABLE
  const updateFilter = useCallback(
    (filterType, value) => {
      setFilters((prev) => {
        const updated = applyCategoryConstraints({
          ...prev,
          [filterType]: value,
        });
        isGlobal && updateUserPreferencesForFilters(userContext, updated);
        return updated;
      });
    },
    [setFilters, userContext, applyCategoryConstraints, isGlobal]
  );

  const updateFilters = useCallback(
    (newFilters) => {
      setFilters((prev) => {
        const merged = {
          ...prev,
          ...newFilters,
        };
        const constrained = applyCategoryConstraints(merged);
        isGlobal && updateUserPreferencesForFilters(userContext, constrained);
        return constrained;
      });
    },
    [setFilters, userContext, applyCategoryConstraints, isGlobal]
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    // Note: resetFilterPreferences preserves firm_user (selected users)
    isGlobal && resetFilterPreferences(userContext);
  }, [setFilters, isGlobal, userContext]);

  // User Selection Actions - STABLE
  const toggleUser = useCallback(
    (userId) => {
      setSelectedUsers((prev) => {
        if (prev.includes(userId)) {
          const next = prev.filter((id) => id !== userId);
          isGlobal && updateFirmUserPreference(userContext, next);
          return next;
        }
        const next = [...prev, userId];
        isGlobal && updateFirmUserPreference(userContext, next);
        return next;
      });
    },
    [setSelectedUsers, userContext, isGlobal]
  );

  const setUsers = useCallback(
    (userIds) => {
      setSelectedUsers(userIds);
      isGlobal && updateFirmUserPreference(userContext, userIds);
    },
    [setSelectedUsers, userContext, isGlobal]
  );

  const clearSelectedUsers = useCallback(() => {
    setSelectedUsers([]);
    isGlobal && updateFirmUserPreference(userContext, []);
  }, [setSelectedUsers, userContext, isGlobal]);

  // Google Events Actions - STABLE
  const toggleGoogleEvents = useCallback(
    (userId) => {
      setGoogleEventsEnabled((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(userId)) {
          newSet.delete(userId);
        } else {
          newSet.add(userId);
        }
        return newSet;
      });
    },
    [setGoogleEventsEnabled]
  );

  const clearGoogleEvents = useCallback(() => {
    setGoogleEventsEnabled(new Set());
  }, [setGoogleEventsEnabled]);

  // Settings Actions - STABLE
  const updateSettings = useCallback(
    (newSettings) => {
      setSettings((prev) => ({
        ...prev,
        ...newSettings,
      }));
    },
    [setSettings]
  );

  // Memoize actions object - STABLE REFERENCE
  // Empty deps because all callbacks have stable dependencies (setState functions)
  const value = useMemo(
    () => ({
      updateFilter,
      updateFilters,
      clearFilters,
      toggleUser,
      setUsers,
      clearSelectedUsers,
      toggleGoogleEvents,
      clearGoogleEvents,
      updateSettings,
    }),
    [
      updateFilter,
      updateFilters,
      clearFilters,
      toggleUser,
      setUsers,
      clearSelectedUsers,
      toggleGoogleEvents,
      clearGoogleEvents,
      updateSettings,
    ]
  );

  return <CalendarActionsContext.Provider value={value}>{children}</CalendarActionsContext.Provider>;
};

export default CalendarActionsContext;
